import { inject, Injectable } from '@angular/core';
import { UpdateEvent } from '@daisi/models/update-event';
import { DataFeature } from '@daisi/models/data-feature';
import { LoggingService } from '@daisi/services/logging/logging.service';
import { UserStateService } from '@daisi/services/user-state/user-state.service';
import { ToastService } from '@ndwnu/design-system';
import { FeatureCollection } from '@generated/backend-api/model/feature-collection';
import { DataGeoJsonFeature } from '@models/data-geojson-feature';

@Injectable({
  providedIn: 'root',
})
export class CombineService {
  private readonly log = inject(LoggingService);
  private readonly userStateService = inject(UserStateService);
  private readonly toastService = inject(ToastService);

  combine(featureCollection: FeatureCollection, updateEvent: UpdateEvent): FeatureCollection {
    const withoutRemoved = this.handleRemoved(featureCollection.features!, updateEvent.removed);
    const withUpdated = this.handleUpdated(withoutRemoved, updateEvent.updated);
    const withCreated = this.handleCreated(withUpdated, updateEvent.created);

    return {
      ...featureCollection,
      features: withCreated,
    } as FeatureCollection;
  }

  private handleRemoved(features: DataGeoJsonFeature<any>[], removedIds: string[]) {
    if (removedIds.some((id) => this.userStateService.state.selectedIncident()?.id === id)) {
      if (this.userStateService.state.detailPanelOpen()) {
        this.toastService.open(
          'Het incident is niet meer beschikbaar, het detailpaneel is gesloten.',
        );
      }
      this.userStateService.state.selectedIncident.set(undefined);
      this.userStateService.state.detailPanelOpen.set(false);
    }

    return features.filter((feature) => !removedIds.includes(String(feature.id)));
  }

  private handleUpdated(
    features: DataGeoJsonFeature<any>[],
    updatedFeatures: DataGeoJsonFeature<any>[],
  ) {
    const withUpdates = features.map((feature) => {
      const featureUpdate = updatedFeatures.find((update) => update.id === feature.id);
      if (!featureUpdate) {
        return feature;
      }
      return {
        ...feature,
        ...featureUpdate,
        properties: {
          ...feature.properties,
          ...featureUpdate.properties,
        },
      };
    });

    // If the currently selected feature was updated, we need to update it in the userstate as well.
    // @ts-ignore
    withUpdates.forEach((feature: DataGeoJsonFeature) => {
      if (feature.properties.id === this.userStateService.state.selectedIncident()?.properties.id) {
        this.userStateService.state.selectedIncident.set(feature);
      }
      if (feature.properties.id === this.userStateService.state.mapPopup()?.feature.properties.id) {
        this.userStateService.state.mapPopup.set({
          ...this.userStateService.state.mapPopup()!,
          feature,
        });
      }
    });

    return withUpdates;
  }

  private handleCreated(features: DataGeoJsonFeature<any>[], createdFeatures: DataFeature[]) {
    const existing = new Set(features.map((feature) => feature.id));
    const duplicates = createdFeatures
      .filter((feature) => existing.has(feature.id))
      .map((feature) => feature.id);
    if (duplicates.length > 0) {
      this.log.debug('A number of created features are already present', duplicates);
    }

    let newFeatures = createdFeatures.filter((created) => !existing.has(created.id));

    newFeatures.forEach(
      (feature) => ((feature as DataGeoJsonFeature<any>).properties.firstSeenMillis = Date.now()),
    );

    return [...features, ...newFeatures];
  }
}
